import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { wasRemembered } from '../services/token'
import api from '../services/api'
import { recordNavEntry } from '../utils/navHistory'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  // Pre-check the box if the previous session was "remember me".  Otherwise
  // default unchecked — the standard secure default.  Same UX in browser
  // and Electron; in Electron the unchecked case is enforced at quit-time
  // by clearing the token from localStorage (see desktop/main.js
  // before-quit handler).
  const [remember, setRemember] = useState(wasRemembered())
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // 2FA step state — when the password step returns totp_required, we stash the
  // challenge token here and show a code input instead of the password form.
  const [totpChallenge, setTotpChallenge] = useState(null)   // { challenge_token } or null
  const [totpCode, setTotpCode] = useState('')

  const { login, completeTotpLogin, completePasskeyLogin } = useAuth()
  const navigate = useNavigate()

  // Feedback #830 — every post-sign-in navigation below uses { replace: true },
  // so this page is REPLACED by the one it leads to and a later ← Back (or the
  // browser's back button) can never land a signed-in user on the sign-in
  // form.  Recording the entry is the belt to that pair of braces: if some
  // other path ever pushes past this page, the global Back button in Layout
  // sees that the entry behind it is /login and goes to Calendar instead.
  useEffect(() => {
    recordNavEntry(window.history.state?.idx ?? 0, '/login')
  }, [])

  // Passkey login — skip password + TOTP entirely.  Requires the user to
  // enter their email first (so the server knows which credentials to
  // present).  Browser prompts for Touch ID / Face ID / security key.
  const handlePasskeyLogin = async () => {
    setError('')
    if (!email) {
      setError('Enter your email first, then click Sign in with passkey')
      return
    }
    setLoading(true)
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser')
      const opt = await api.post('/api/auth/passkey/authentication-options',
        { email, remember })
      if (!opt.data.options.allowCredentials || opt.data.options.allowCredentials.length === 0) {
        // Server returned zero credentials — either the email doesn't exist
        // OR the user hasn't enrolled any passkeys.  Don't distinguish
        // (enumeration hardening).
        setError('No passkey found for this account.  Sign in with password instead.')
        return
      }
      const credential = await startAuthentication({ optionsJSON: opt.data.options })
      const verify = await api.post('/api/auth/passkey/verify', {
        credential,
        challenge_token: opt.data.challenge_token,
      })
      const data = verify.data
      const result = await completePasskeyLogin(data, remember)
      // Same #830 routing as the password + TOTP paths: a flagged account goes
      // to /profile to set its own password (finishLogin supplies the flag
      // from /me because the passkey response doesn't carry it).
      navigate(result?.must_change_password ? '/profile' : '/dashboard', { replace: true })
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Passkey sign-in failed'
      if (/NotAllowedError|operation either timed out/i.test(msg)) {
        setError('Sign-in cancelled')
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password, remember)
      if (result.totp_required) {
        // Hand off to the TOTP step — auth not finished yet.
        setTotpChallenge({ challenge_token: result.totp_challenge_token })
        setTotpCode('')
      } else if (result.must_change_password) {
        navigate('/profile', { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  const handleTotpSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await completeTotpLogin(totpChallenge.challenge_token, totpCode, remember)
      if (result.used_backup_code) {
        // Soft warning — the user just spent one of their 10 backup codes.
        setError(`⚠️ Signed in with a backup code. ${result.backup_codes_remaining} left.`)
      }
      if (result.must_change_password) navigate('/profile', { replace: true })
      else navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code')
    } finally {
      setLoading(false)
    }
  }

  const cancelTotp = () => {
    setTotpChallenge(null); setTotpCode(''); setError('')
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-md">

        {/* Logo / Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-600">Omnia Practice Management</h1>
          <p className="text-gray-500 mt-2">Practice Management System</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-6 text-sm">
            {error}
          </div>
        )}

        {!totpChallenge ? (
          /* ── Step 1: password ─────────────────────────────────────── */
          <form onSubmit={handlePasswordSubmit} className="space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="login-email" name="email" type="email" autoComplete="username"
                value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="you@lawfirm.com"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  id="login-password" name="password"
                  type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full border border-gray-300 rounded-lg pl-4 pr-12 py-2.5
                             focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPassword(s => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600">
                  {showPassword ? (
                    /* eye-off */
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    /* eye */
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              Keep me signed in on this device
            </label>

            <button type="submit" disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white
                         font-semibold rounded-lg py-2.5 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:underline">
                Forgot password?
              </Link>
            </div>

            {/* Passkey login — alternative to password.  Only shown when the
                browser supports WebAuthn; on older browsers it silently
                collapses.  Requires email above so the server can look up
                allowCredentials.  Skips TOTP entirely since passkey is
                already a strong second factor cryptographically. */}
            {typeof window !== 'undefined' && !!window.PublicKeyCredential && (
              <>
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">or</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={loading}
                  className="w-full bg-white border border-gray-300 hover:bg-gray-50
                             text-gray-700 font-semibold rounded-lg py-2.5 transition-colors
                             flex items-center justify-center gap-2
                             disabled:opacity-50 disabled:cursor-not-allowed">
                  🔑 Sign in with passkey
                </button>
              </>
            )}
          </form>
        ) : (
          /* ── Step 2: TOTP / backup code ──────────────────────────── */
          <form onSubmit={handleTotpSubmit} className="space-y-5">
            <div className="bg-blue-50 text-blue-700 rounded-lg p-3 text-sm">
              🔐 Enter the 6-digit code from your authenticator app — or a 10-character backup code if you don't have access to it.
            </div>
            <div>
              <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700 mb-1">Authentication code</label>
              <input
                id="totp-code" type="text" autoFocus autoComplete="one-time-code"
                inputMode="text"
                value={totpCode} onChange={e => setTotpCode(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 font-mono tracking-widest text-center text-lg
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="123 456"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={cancelTotp}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Back
              </button>
              <button type="submit" disabled={loading || !totpCode}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white
                           font-semibold rounded-lg py-2.5 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          Have an invite code? <Link to="/register" className="text-primary-600 hover:underline font-medium">Register</Link>
        </p>

        <p className="text-center text-xs text-gray-400 mt-4">
          Omnia Practice Management © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
